/**
 * Order-independent serialisation, for comparing two models that took different
 * routes to get here.
 *
 * `JSON.stringify` writes keys in insertion order, and a model parsed out of an
 * API response is rebuilt by zod in *schema* order while one held in the editor
 * or read back from `localStorage` keeps whatever order it was built with. The
 * two are the same model and stringify to different bytes, so a plain
 * `JSON.stringify(a) === JSON.stringify(b)` reports changes nobody made — an
 * "unsaved draft" banner on a scenario that was just saved, a conflict dialog
 * claiming every package differs.
 *
 * Arrays keep their order, because in a model order is meaning: `sortOrder`,
 * year rows and the line-item sequence all ride on it.
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;

  const entries = Object.entries(value as Record<string, unknown>)
    // `undefined` is absent, not a value: `JSON.stringify` drops such keys, and
    // a round trip through storage would otherwise look like an edit.
    .filter(([, entry]) => entry !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(",")}}`;
}

/** Deep equality by the same rules. */
export const sameShape = (a: unknown, b: unknown): boolean =>
  stableStringify(a) === stableStringify(b);
